import { selectCurrentHotelId, selectCurrentHotelName } from 'accessors'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import Dialog from 'components/ReusableComponents/Dialog'
import SmsIcon from 'components/ReusableComponents/Icons/Sms'
import { inPropertyAudiences, inPropertyAudienceTypes } from 'features/Announcements'
import AnnouncementConfirmation from 'features/Announcements/AnnouncementConfirmation'
import MainTemplate from 'features/CustomCampaigns/EditCampaign/MainTemplate'
import { EditPromo } from 'features/ManageCampaigns/EditPromoDialog'
import { TextPreview, TextPreviewBubble } from 'features/ManageCampaigns/TextPreview'
import { getImgUrlFromCDN } from 'features/ManageCampaigns/utils/campaignHelpers'
import { mediaTags } from 'features/ManageCampaigns/utils/constants'
import useFirstRender from 'hooks/useFirstRender'
import usePlausible from 'hooks/usePlausible'
import html2canvas from 'html2canvas'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import announcementApi from 'services/api/announcement'
import campaignApi from 'services/api/campaign'

import styles from './EditTextDialog.module.scss'

const steps = {
  mainTemplate: 'mainTemplate',
  mainPromo: 'mainPromo'
}

const stepNames = {
  mainTemplate: 'Text content',
  mainPromo: 'Promotion (optional)'
}

function getSteps(audience) {
  if (audience === inPropertyAudienceTypes.DayBeforeCheckOut) {
    return steps
  }

  return { mainTemplate: steps.mainTemplate }
}

const mediaTag = mediaTags.announcement

const EditTextDialog = ({
  onClose,
  announcementType,
  audience,
  onMessageSent,
  defaultText = '',
  totalEligibleForText
}) => {
  const { postEvent, plausibleEvents } = usePlausible()

  function handleTextChange(text) {
    setMessageTemplate(text.replace(/inviteLink__\)(\S)/, 'inviteLink__) $1'))
  }

  const [activeStep, setActiveStep] = useState(steps.mainTemplate)
  const [nextStep, setNextStep] = useState()

  const [usedAllTags, setUsedAllTags] = useState(true)
  const [messageMedia, setMessageMedia] = useState()
  const [messageTemplate, setMessageTemplate] = useState(defaultText)

  const [promotionCode, setPromotionCode] = useState()
  const [maxDiscountPercentage, setMaxDiscountPercentage] = useState()
  const [minNightsToStay, setMinNightsToStay] = useState()
  const [promoTagline, setPromoTagline] = useState('Sample promo taglne')
  const [hasPromoValidityDate, setHasPromoValidityDate] = useState(false)
  const [promoValidityStartDate, setPromoValidityStartDate] = useState()
  const [promoValidityEndDate, setPromoValidityEndDate] = useState()

  const userName = useSelector(s => s.login.currentUser.name)
  const isHotelSetupCompleted = useSelector(s => s.hotelDetails.isPreOnboardingCompleted)
  const hotelName = useSelector(selectCurrentHotelName)
  const hotelId = useSelector(selectCurrentHotelId)

  const [isSendingTestMessage, setIsSendingTestMessage] = useState(false)

  const { addToast } = useToasts()

  useFirstRender(() => {
    postEvent(plausibleEvents.ViewAnnouncementEditor)
  }, [postEvent, plausibleEvents.ViewAnnouncementEditor])

  useEffect(() => {
    function getNextStep() {
      if (
        activeStep === steps.mainTemplate &&
        audience === inPropertyAudienceTypes.DayBeforeCheckOut
      ) {
        return steps.mainPromo
      }

      return null
    }

    setNextStep(getNextStep())
  }, [activeStep, audience])

  const [isShowingConfirmation, setIsShowingConfirmation] = useState(false)

  if (isShowingConfirmation) {
    return (
      <AnnouncementConfirmation
        audience={audience}
        totalEligibleForText={totalEligibleForText}
        announcementType={announcementType}
        handleCancel={() => setIsShowingConfirmation(false)}
        handleConfirm={sendAnnouncement}
      />
    )
  }

  function sendTestMessage() {
    setIsSendingTestMessage(true)

    announcementApi
      .sendTestMessage({
        audience,
        sendExactlyOn: null,
        messageTemplate,
        mediaUrls: [messageMedia],
        promotionCode,
        minNightsToStay,
        promotionTagline: promoTagline,
        maxDiscountPercentage,
        promoValidityStartDate,
        promoValidityEndDate,
        hasPromoValidityDate
      })
      .then(() => {
        addToast('Test message sent', { appearance: 'success' })
      })
      .catch(() => {
        addToast(`Couldn't send the test message`, { appearance: 'error' })
      })
      .finally(() => {
        setIsSendingTestMessage(false)
      })
  }

  function sendAnnouncement({ date = null, repeatCount }) {
    postEvent(plausibleEvents.CreateAnnouncement)

    const endpoint = repeatCount > 0 ? announcementApi.addRecurring : announcementApi.add

    endpoint({
      audience,
      sendExactlyOn: date,
      messageTemplate,
      mediaUrls: [messageMedia],
      repeatCount,
      promotionCode,
      minNightsToStay,
      promotionTagline: promoTagline,
      maxDiscountPercentage,
      promoValidityStartDate,
      promoValidityEndDate,
      hasPromoValidityDate
    }).then(res => {
      if (Array.isArray(res)) {
        res.forEach(r => {
          const contentId = r.details.contentId

          html2canvas(document.querySelector('#announcement-estimation')).then(canvas => {
            const screenshot = canvas.toDataURL('image/png')

            campaignApi.postCampaignEstimationScreenshot({
              contentId,
              screenshot
            })
            onMessageSent(res)
          })
        })
      } else {
        const contentId = res.details.contentId

        html2canvas(document.querySelector('#announcement-estimation')).then(canvas => {
          const screenshot = canvas.toDataURL('image/png')

          campaignApi.postCampaignEstimationScreenshot({
            contentId,
            screenshot
          })

          onMessageSent([res])
        })
      }
      onClose()
    })
  }

  function handleScheduleOrSend() {
    if (!isHotelSetupCompleted) {
      addToast(
        'Your hotel setup is not completed yet. Complete your setup to send announcements.',
        {
          appearance: 'error',
          autoDismiss: true
        }
      )

      return
    }

    setIsShowingConfirmation(true)
  }

  return (
    <Dialog>
      <div className={styles.resolutionWrapper}>
        <div className={styles.dialogWrapper}>
          <div className={styles.mainContent}>
            {Object.values(getSteps(audience)).length > 1 && (
              <div className={styles.navigationSidebar}>
                <div>
                  <div className={styles.dialogDesc}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div>
                        <span className={styles.deliveryName}>Announcement</span>
                        <p className={styles.status}>
                          {inPropertyAudiences[audience].name}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.controls}>
                  <div className={styles.steps}>
                    {Object.values(getSteps(audience)).map(step => (
                      <button
                        key={step}
                        onClick={() => setActiveStep(step)}
                        className={step === activeStep ? styles.activeStep : styles.step}
                      >
                        <span className={styles.paginatorNo}>{stepNames[step]}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className={styles.editTextHolder}>
              <div className={styles.editTextContent}>
                {Object.values(getSteps(audience)).length === 1 && (
                  <div className={styles.title}>
                    <h2 className={styles.dialogTitle}>Announcement</h2>
                    <div className={styles.dialogDesc}>
                      {inPropertyAudiences[audience].name}
                    </div>
                  </div>
                )}
                {activeStep === steps.mainTemplate && (
                  <MainTemplate
                    messageTemplate={messageTemplate}
                    onTextChange={handleTextChange}
                    setUsedAllTags={setUsedAllTags}
                    usedAllTags={usedAllTags}
                    messageMedia={messageMedia}
                    setMessageMedia={setMessageMedia}
                    isAllTagsRequired={true}
                    mediaTag={mediaTag}
                    requiredTagIds={['__guestName__', '__hotelName__']}
                    isMediaRequired={false}
                  />
                )}
                {activeStep === steps.mainPromo && (
                  <EditPromo
                    promoCode={promotionCode}
                    setPromoCode={setPromotionCode}
                    minNights={minNightsToStay}
                    setMinNights={setMinNightsToStay}
                    maxDiscount={maxDiscountPercentage}
                    setMaxDiscount={setMaxDiscountPercentage}
                    setPromoTagline={setPromoTagline}
                    promoTagline={promoTagline}
                    hasPromoValidityDateControls={false}
                    hasPromoValidityDate={hasPromoValidityDate}
                    setHasPromoValidityDate={setHasPromoValidityDate}
                    promoValidityStartDate={promoValidityStartDate}
                    setPromoValidityStartDate={setPromoValidityStartDate}
                    promoValidityEndDate={promoValidityEndDate}
                    setPromoValidityEndDate={setPromoValidityEndDate}
                    promoToggleLabel="Add link with promotion"
                  />
                )}
              </div>
              <div className={styles.submitHolder}>
                <SecondaryRegularButton onClick={() => onClose()}>
                  Cancel
                </SecondaryRegularButton>
                {nextStep ? (
                  <PrimaryRegularButton
                    className={styles.nextButton}
                    onClick={() => setActiveStep(nextStep)}
                    disabled={messageTemplate.length === 0 || !usedAllTags}
                  >
                    Next
                  </PrimaryRegularButton>
                ) : (
                  <PrimaryRegularButton
                    disabled={messageTemplate.length === 0 || !usedAllTags}
                    onClick={handleScheduleOrSend}
                  >
                    {announcementType === 'schedule' ? 'Schedule' : 'Send'} announcement
                  </PrimaryRegularButton>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.previewHolder}>
          <div className={styles.previewControlHolder}>
            <button onClick={sendTestMessage} disabled={isSendingTestMessage}>
              <SmsIcon />
              {isSendingTestMessage ? 'Sending...' : 'Send yourself a test message'}
            </button>
          </div>
          <TextPreview>
            <TextPreviewBubble
              text={messageTemplate}
              defaultData={{ userName, hotelName }}
              hasOptOutText
              hasLink={!!promotionCode}
            />
            {messageMedia && (
              <img
                src={getImgUrlFromCDN(messageMedia, mediaTag, hotelId)}
                alt="campaign media"
                className={styles.campaignMedia}
              />
            )}
          </TextPreview>
        </div>
      </div>
    </Dialog>
  )
}

export default EditTextDialog
